import { useCallback } from 'react';
import { colors } from '../../../../../../../colors';
import ColoredCheckbox from '../../../../../../../components/ColoredCheckbox';
import { ChartType } from '../../../../../types';

import './index.css';

interface Props {
  selectedChartTypes: ChartType[];
  setSelectedChartTypes: React.Dispatch<React.SetStateAction<ChartType[]>>;
  getChartColor: (chartType: ChartType) => {
    fill: string;
    stroke: string;
  };
  areTransactionProfitsVisible: boolean;
  toggleTransactionProfits: () => void;
  isTransactionsSelected: boolean;
  toggleTransactions: () => void;
}

const ChartTypeSelector = (props: Props) => {
  const {
    selectedChartTypes,
    setSelectedChartTypes,
    getChartColor,
    areTransactionProfitsVisible,
    toggleTransactionProfits,
    isTransactionsSelected,
    toggleTransactions,
  } = props;

  const isPriceSelected = !!selectedChartTypes.find(
    (c) => c === ChartType.NoAlgoritmicProgression,
  );
  const isStableBalanceSelected = !!selectedChartTypes.find(
    (c) => c === ChartType.StableBalance,
  );
  const isAssetBalanceSelected = !!selectedChartTypes.find(
    (c) => c === ChartType.AssetBalance,
  );

  const handleChartTypePress = useCallback(
    (chartType: ChartType) => {
      setSelectedChartTypes((prev) => {
        const current = [...prev];
        const chartTypeIndex = current.findIndex((c) => c === chartType);

        if (chartTypeIndex === -1) {
          current.push(chartType);
          return current;
        }

        if (selectedChartTypes.length === 1) {
          return current;
        }

        current.splice(chartTypeIndex, 1);
        return current;
      });
    },
    [selectedChartTypes.length, setSelectedChartTypes],
  );

  return (
    <div className="chart-type-selector-root">
      <div className="profit-chart-options-item chart-type-selector-item">
        <ColoredCheckbox
          value={isStableBalanceSelected}
          handleValueChange={() =>
            handleChartTypePress(ChartType.StableBalance)
          }
          color={
            isStableBalanceSelected
              ? getChartColor(ChartType.StableBalance).stroke
              : colors.gray
          }
          size={12}
        />
        <span
          className="profit-chart-options-label chart-type-selector-item-label"
          style={{
            color: isStableBalanceSelected
              ? getChartColor(ChartType.StableBalance).stroke
              : colors.gray,
          }}
        >
          Stable Balance
        </span>
      </div>
      <div className="profit-chart-options-item chart-type-selector-item">
        <ColoredCheckbox
          value={isAssetBalanceSelected}
          handleValueChange={() => handleChartTypePress(ChartType.AssetBalance)}
          color={
            isAssetBalanceSelected
              ? getChartColor(ChartType.AssetBalance).stroke
              : colors.gray
          }
          size={12}
        />
        <span
          className="profit-chart-options-label chart-type-selector-item-label"
          style={{
            color: isAssetBalanceSelected
              ? getChartColor(ChartType.AssetBalance).stroke
              : colors.gray,
          }}
        >
          Asset Balance
        </span>
      </div>
      <div className="profit-chart-options-item chart-type-selector-item">
        <ColoredCheckbox
          value={isPriceSelected}
          handleValueChange={() =>
            handleChartTypePress(ChartType.NoAlgoritmicProgression)
          }
          color={
            isPriceSelected
              ? getChartColor(ChartType.NoAlgoritmicProgression).stroke
              : colors.gray
          }
          size={12}
        />
        <span
          className="profit-chart-options-label chart-type-selector-item-label"
          style={{
            color: isPriceSelected
              ? getChartColor(ChartType.NoAlgoritmicProgression).stroke
              : colors.gray,
          }}
        >
          Price
        </span>
      </div>
      <div className="profit-chart-options-item chart-type-selector-item">
        <ColoredCheckbox
          value={isTransactionsSelected}
          handleValueChange={toggleTransactions}
          color={isTransactionsSelected ? colors.ligthBlue : colors.gray}
          size={12}
        />
        <span
          className="profit-chart-options-label chart-type-selector-item-label"
          style={{
            color: isTransactionsSelected ? colors.ligthBlue : colors.gray,
          }}
        >
          Transactions
        </span>
      </div>
      <div className="profit-chart-options-item chart-type-selector-item">
        <ColoredCheckbox
          value={areTransactionProfitsVisible}
          handleValueChange={toggleTransactionProfits}
          color={areTransactionProfitsVisible ? colors.green : colors.gray}
          size={12}
        />
        <span
          className="profit-chart-options-label chart-type-selector-item-label"
          style={{
            color: areTransactionProfitsVisible ? colors.green : colors.gray,
          }}
        >
          Transaction Profits
        </span>
      </div>
    </div>
  );
};

export default ChartTypeSelector;
